// Blog posts — long-form Canadian security industry content
// targeting long-tail SEO keywords and backlink acquisition.

export const BLOG_POSTS = [
  {
    slug: "bc-security-worker-license-guide-2026",
    title: "The BC Security Worker License, Explained (2026 Update)",
    excerpt:
      "Everything property managers, event producers, and site supers need to verify before hiring a security guard in British Columbia — with a checklist you can send to any vendor.",
    date: "2026-11-02",
    readTime: "8 min",
    category: "Compliance",
    author: "Maze Security Operations",
    image: "https://images.pexels.com/photos/8285775/pexels-photo-8285775.jpeg",
    keywords: [
      "BC Security Worker License 2026",
      "BC Security Programs Division",
      "licensed security guards BC verification",
      "hire security guards British Columbia checklist",
    ],
    body: [
      { h: "What the BC Security Worker License actually covers", p: "Issued by the Ministry of Public Safety and Solicitor General under the Security Services Act, the BC Security Worker License is the mandatory credential for any person performing security work in British Columbia. It's not the same as a business license — that's the Security Business License, held by the company. Both must be valid, and both are searchable on the Security Programs Division public registry." },
      { h: "The three tiers property managers confuse", p: "There's the individual Security Worker License (SWL) held by the officer, the Advanced Security Training (AST) endorsement required for licensed premises and larger crowd-facing events, and the Basic Security Training (BST) certificate that precedes the SWL. When you hire a company, you should be seeing all three represented on the officers assigned to your site — and the AST specifically for anyone doing venue or licensed-premises work." },
      { h: "How to actually verify a company", p: "Ask for the company's Security Business License number, then look it up on the SPD registry. Ask for a sample officer's SWL number and check the same registry. Ask for proof of $2M+ CGL insurance and a WorkSafeBC clearance letter. Any legitimate BC security company will send all three without hesitation." },
      { h: "Red flags", p: "Sub-contracting to unnamed third parties. Missing WorkSafeBC coverage. Refusal to provide the officer's individual SWL number. Officers arriving in unmarked vehicles without ID. All four are grounds to end the contract immediately." },
    ],
  },
  {
    slug: "mining-security-british-columbia-2026",
    title: "Mining Security in British Columbia: FIFO, ERPs, and Environmental Protest Response",
    excerpt:
      "Copper, gold, and coal operations from the Kootenays to the Northwest have unique security requirements. A field-level walk-through of what actually works.",
    date: "2026-10-15",
    readTime: "12 min",
    category: "Mining",
    author: "Maze Security Operations",
    image: "https://images.unsplash.com/photo-1628487749130-2d41acb1802a",
    keywords: [
      "mining security British Columbia",
      "FIFO security officers BC",
      "mine site ERP security",
      "environmental protest response mine BC",
    ],
    body: [
      { h: "The mineral belt is not one job", p: "A copper open-pit in Highland Valley has different security needs than a coal operation in the Elk Valley or a gold exploration camp on Vancouver Island. The common ground: gatehouse operations, contractor screening, and ERP integration. The differences: blast windows, tailings facility patrols, environmental monitoring escort, and camp culture." },
      { h: "FIFO rotations that actually retain officers", p: "We run 14/14 and 21/7 rotations for most BC mining clients. Anything shorter burns officers out; anything longer burns clients out. Rotation logistics need to sync with your charter or scheduled flight schedule from Vancouver, Kelowna, or Prince George — and we handle the ground transport at both ends." },
      { h: "Protest and blockade planning", p: "Any BC mining operation should have a lawful-response protocol drafted in advance with legal counsel. Our role is de-escalation-first, documentation-heavy, and coordination with RCMP if required. We do not use force. We record, we witness, we buy time for lawyers to act." },
    ],
  },
  {
    slug: "cannabis-security-canada-cannabis-act",
    title: "Cannabis Facility Security in Canada — Cannabis Act Compliance for LPs and Retail",
    excerpt:
      "Health Canada and provincial LDB requirements for cannabis cultivation, processing, and retail — plus the practical security setup that keeps your licence.",
    date: "2026-09-22",
    readTime: "10 min",
    category: "Cannabis",
    author: "Maze Security Operations",
    image: "https://images.unsplash.com/photo-1603909223429-69bb7101f420",
    keywords: [
      "cannabis security Canada",
      "Cannabis Act security compliance",
      "licensed producer security BC",
      "cannabis retail security British Columbia",
    ],
    body: [
      { h: "Health Canada vs. BC LDB — what each cares about", p: "Health Canada regulates cultivation, processing, and packaging under the Cannabis Regulations — expect intrusion detection, restricted-access rooms, video retention (1 year minimum), and personnel screening. The BC LDB regulates retail — cash handling, ID checks, product theft response, and public order on premises. Different focuses, both non-negotiable." },
      { h: "Video retention and PIPEDA", p: "Retain video for the minimum period Health Canada requires, but be careful — PIPEDA applies to any recording that captures identifiable individuals, including staff. Post signage, limit access, and document your retention schedule." },
      { h: "The retail floor risk profile", p: "Cannabis retail is a magnet for smash-and-grab because product is high-value, portable, and consumable. Overnight alarm response, mag-locked vestibules, and daytime plain-clothes coverage during known-vulnerable hours (early evening, Friday nights) are the setup we run for BC LDB retailers." },
    ],
  },
  {
    slug: "fire-watch-ahj-bc-checklist",
    title: "Fire Watch in BC: The AHJ Checklist That Keeps Occupancy Legal",
    excerpt:
      "When sprinklers are drained or an alarm system is offline, fire watch keeps your building open. Here's the checklist most contractors miss.",
    date: "2026-08-10",
    readTime: "6 min",
    category: "Fire Watch",
    author: "Maze Security Operations",
    image: "https://images.unsplash.com/photo-1601058272104-e6d2fcd4dbcf",
    keywords: [
      "fire watch BC checklist",
      "AHJ fire watch British Columbia",
      "impaired fire system BC",
      "hot work fire watch Canada",
    ],
    body: [
      { h: "AHJ notification before the watch starts", p: "The Authority Having Jurisdiction — usually your municipal fire prevention officer — needs to know before the impairment starts, not after. Skip this step and you risk occupancy violations, insurance denials, and evacuation orders." },
      { h: "The 15/30/60-minute round decision", p: "Round frequency depends on the impairment type. Full alarm impairment usually mandates 30-minute rounds. Sprinkler impairment can be hourly. Hot work is 15-minute rounds during work and 60 minutes for the hour after — check with your AHJ." },
      { h: "Documentation the AHJ will actually ask for", p: "Timestamped log per round, officer name and SWL number, extinguisher placement verification, communication log with any 911 calls, and photo evidence of round completion. Digital logs beat paper — assume the AHJ will audit." },
    ],
  },
  {
    slug: "event-security-bc-2026-permits",
    title: "Event Security in BC: Permits, AST Requirements, and the 100-Person Threshold",
    excerpt:
      "Everything you need to know about staffing crowd security for BC events — from a 50-person wedding to a 10,000-person festival.",
    date: "2026-07-18",
    readTime: "9 min",
    category: "Events",
    author: "Maze Security Operations",
    image: "https://images.unsplash.com/photo-1556764420-e37ef4cdfa5c",
    keywords: [
      "event security BC permits",
      "AST security officers BC",
      "festival security British Columbia",
      "wedding security BC",
    ],
    body: [
      { h: "Where AST becomes mandatory", p: "Advanced Security Training is required for officers working licensed premises and larger crowd-facing events. Most BC municipalities require AST-endorsed officers for any event serving alcohol above 100 attendees. Confirm with your event permit officer." },
      { h: "Ratios that actually work", p: "For alcohol-serving events, plan 1 officer per 100 attendees minimum, with a floor of 3 officers. For general-admission concerts, closer to 1:75. For VIP-heavy corporate events, 1:50. These are staffing floors, not ceilings." },
      { h: "The pre-event walk-through", p: "Two weeks out at minimum. Cover ingress, egress, first-aid station placement, radio coverage, staging security, VIP routes, and post-event escorted egress. Skip the walk and you're operating blind." },
    ],
  },
  {
    slug: "worksafebc-security-industry-2026",
    title: "WorkSafeBC and the Security Industry: What Clients Should Know",
    excerpt:
      "Why WorkSafeBC clearance letters matter, what they mean, and how they protect the client — not just the officer.",
    date: "2026-06-05",
    readTime: "7 min",
    category: "Compliance",
    author: "Maze Security Operations",
    image: "https://images.pexels.com/photos/27299956/pexels-photo-27299956.jpeg",
    keywords: [
      "WorkSafeBC security industry",
      "WCB clearance letter security company BC",
      "security company insurance British Columbia",
    ],
    body: [
      { h: "What a WorkSafeBC clearance letter proves", p: "It confirms the security company is up-to-date on WCB premium payments — meaning if an officer is injured on your property, WCB (not you) is the first-line insurer. Without it, you're exposed under the Workers Compensation Act." },
      { h: "The renewal cadence", p: "Clearance letters are typically valid 15–60 days. Ask for a fresh one every quarter, and store it with your vendor documentation." },
      { h: "Red flag — refusal to provide", p: "Any security company that refuses or delays providing a clearance letter is telling you something. Move on." },
    ],
  },
];

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
